import React, { useState } from 'react';
import Drawer from '../common/Drawer';
import FormField from '../common/FormField';
import Button from '../common/Button';
import PasswordRequirements from '../common/PasswordRequirements';
import { showAlert } from '../../utils/sweetAlertUtils';
import handleChange from '../../utils/formUtils/handleChange';
import { useMemberApi } from '../../hooks/useMemberApi';
import { MemberResetPasswordDTO } from '../../services/apiClient';

interface ChangePasswordDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  memberName?: string;
  memberId: string; // Added memberId prop
}

interface PasswordFormData {
  password: string;
  confirmPassword: string;
}

const ChangePasswordDrawer: React.FC<ChangePasswordDrawerProps> = ({
  isOpen,
  onClose,
  memberName = 'Member',
  memberId
}) => {
  const [formData, setFormData] = useState<PasswordFormData>({
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { resetPassword, isLoading } = useMemberApi();

  // Password validation
  const validatePassword = (password: string) => {
    return {
      length: password.length >= 6,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /\d/.test(password)
    };
  };

  const validation = validatePassword(formData.password);
  const isValidPassword = Object.values(validation).every(Boolean);

  // Handle form changes with the common utility
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    handleChange(e, setFormData, (updated) => {
      // Clear errors when user starts typing
      const fieldName = Object.keys(updated)[0];
      if (errors[fieldName]) {
        setErrors(prev => ({
          ...prev,
          [fieldName]: ''
        }));
      }
    });
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!isValidPassword) {
      newErrors.password = 'Password does not meet requirements';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirm password is required';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        const resetPasswordData: MemberResetPasswordDTO = {
          memberId: memberId,
          newPassword: formData.password
        };

        const success = await resetPassword(resetPasswordData);
        onClose();
        if (success) {
          await showAlert({
            title: 'Success!',
            text: `Password has been changed for ${memberName}`,
            icon: 'success',
            confirmButtonText: 'OK'
          });

          // Reset form
          setFormData({ password: '', confirmPassword: '' });
          setErrors({});
        } else {
          throw new Error('Password reset failed');
        }
      } catch (error) {
        await showAlert({
          title: 'Error',
          text: 'Failed to change password. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  const handleClose = () => {
    setFormData({ password: '', confirmPassword: '' });
    setErrors({});
    onClose();
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      title={`Change Password - ${memberName}`}
      size="small"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Enter a new password for this member. The password must meet all security requirements.
        </div>

        <FormField
          id="password"
          label="New Password"
          type="password"
          value={formData.password}
          onChange={handleFormChange}
          placeholder="Enter new password"
          required
          error={errors.password}
        />

        <PasswordRequirements validation={validation} />

        <FormField
          id="confirmPassword"
          label="Confirm Password"
          type="password"
          value={formData.confirmPassword}
          onChange={handleFormChange}
          placeholder="Confirm new password"
          required
          error={errors.confirmPassword}
        />

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!formData.password || !formData.confirmPassword || isLoading}
          >
            {isLoading ? 'Changing Password...' : 'Change Password'}
          </Button>
        </div>
      </form>
    </Drawer>
  );
};

export default ChangePasswordDrawer;